import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [filter, setFilter] = useState('all'); // all, transactions, users, system
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  useEffect(() => {
    // Initial activities
    const initialActivities = [
      {
        id: 'act-1',
        type: 'transaction',
        title: 'New Sale Recorded',
        description: 'TechCorp Solutions submitted $15,750 sale',
        user: 'John Smith',
        timestamp: new Date(Date.now() - 120000),
        icon: 'DollarSign',
        iconColor: 'text-success',
        iconBg: 'bg-success/10'
      },
      {
        id: 'act-2',
        type: 'user',
        title: 'User Login',
        description: 'Sarah Johnson logged into the system',
        user: 'Sarah Johnson',
        timestamp: new Date(Date.now() - 180000),
        icon: 'LogIn',
        iconColor: 'text-primary',
        iconBg: 'bg-primary/10'
      },
      {
        id: 'act-3',
        type: 'system',
        title: 'Data Sync Complete',
        description: 'Global Enterprises data synchronized successfully',
        user: 'System',
        timestamp: new Date(Date.now() - 240000),
        icon: 'RefreshCw',
        iconColor: 'text-secondary',
        iconBg: 'bg-secondary/10'
      },
      {
        id: 'act-4',
        type: 'transaction',
        title: 'Collection Updated',
        description: 'Innovation Labs recorded $8,500 collection',
        user: 'Mike Davis',
        timestamp: new Date(Date.now() - 300000),
        icon: 'CreditCard',
        iconColor: 'text-success',
        iconBg: 'bg-success/10'
      },
      {
        id: 'act-5',
        type: 'user',
        title: 'Data Entry Started',
        description: 'Emma Wilson began daily data entry process',
        user: 'Emma Wilson',
        timestamp: new Date(Date.now() - 420000),
        icon: 'Edit',
        iconColor: 'text-primary',
        iconBg: 'bg-primary/10'
      },
      {
        id: 'act-6',
        type: 'system',
        title: 'Alert Generated',
        description: 'Zero collection alert triggered for Future Systems',
        user: 'System',
        timestamp: new Date(Date.now() - 480000),
        icon: 'AlertTriangle',
        iconColor: 'text-warning',
        iconBg: 'bg-warning/10'
      },
      {
        id: 'act-7',
        type: 'transaction',
        title: 'Sale Validated',
        description: 'Acme Corporation sale of $22,300 validated',
        user: 'Admin',
        timestamp: new Date(Date.now() - 540000),
        icon: 'CheckCircle',
        iconColor: 'text-success',
        iconBg: 'bg-success/10'
      },
      {
        id: 'act-8',
        type: 'user',
        title: 'User Logout',
        description: 'Robert Chen logged out of the system',
        user: 'Robert Chen',
        timestamp: new Date(Date.now() - 600000),
        icon: 'LogOut',
        iconColor: 'text-muted-foreground',
        iconBg: 'bg-muted'
      }
    ];

    setActivities(initialActivities);
  }, []);

  useEffect(() => {
    // Simulate real-time activity updates
    const interval = setInterval(() => {
      const activityTypes = [
        {
          type: 'transaction',
          titles: ['New Sale Recorded', 'Collection Updated', 'Sale Validated', 'Payment Processed'],
          descriptions: [
            'TechCorp Solutions submitted $12,450 sale',
            'Global Enterprises recorded $9,800 collection',
            'Innovation Labs sale of $18,200 validated',
            'Future Systems processed $6,750 payment'
          ],
          users: ['John Smith', 'Sarah Johnson', 'Mike Davis', 'Emma Wilson'],
          icon: 'DollarSign',
          iconColor: 'text-success',
          iconBg: 'bg-success/10'
        },
        {
          type: 'user',
          titles: ['User Login', 'Data Entry Started', 'Report Generated', 'User Logout'],
          descriptions: [
            'Alex Thompson logged into the system',
            'Lisa Park began daily data entry process',
            'Monthly report generated by system',
            'David Kim logged out of the system'
          ],
          users: ['Alex Thompson', 'Lisa Park', 'David Kim', 'Admin'],
          icon: 'User',
          iconColor: 'text-primary',
          iconBg: 'bg-primary/10'
        },
        {
          type: 'system',
          titles: ['Data Sync Complete', 'Backup Created', 'Alert Generated', 'System Update'],
          descriptions: [
            'Acme Corporation data synchronized successfully',
            'Daily backup completed successfully',
            'Low collection alert triggered',
            'System performance optimized'
          ],
          users: ['System', 'System', 'System', 'System'],
          icon: 'Settings',
          iconColor: 'text-secondary',
          iconBg: 'bg-secondary/10'
        }
      ];

      const randomType = activityTypes[Math.floor(Math.random() * activityTypes.length)];
      const randomIndex = Math.floor(Math.random() * randomType.titles.length);

      const newActivity = {
        id: `act-${Date.now()}`,
        type: randomType.type,
        title: randomType.titles[randomIndex],
        description: randomType.descriptions[randomIndex],
        user: randomType.users[randomIndex],
        timestamp: new Date(),
        icon: randomType.icon,
        iconColor: randomType.iconColor,
        iconBg: randomType.iconBg
      };

      setActivities(prev => [newActivity, ...prev.slice(0, 19)]); // Keep only 20 activities
    }, 20000); // Add new activity every 20 seconds

    return () => clearInterval(interval);
  }, []);

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - timestamp) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const filteredActivities = activities.filter(activity => 
    filter === 'all' || activity.type === filter
  );

  const filterOptions = [
    { value: 'all', label: 'All Activities', icon: 'Activity' },
    { value: 'transactions', label: 'Transactions', icon: 'DollarSign' },
    { value: 'users', label: 'User Actions', icon: 'Users' },
    { value: 'system', label: 'System Events', icon: 'Settings' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Activity" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Activity Feed</h3>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant={isAutoScroll ? "default" : "outline"}
              size="sm"
              onClick={() => setIsAutoScroll(!isAutoScroll)}
            >
              <Icon name={isAutoScroll ? "Pause" : "Play"} size={14} />
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`flex items-center space-x-2 px-3 py-2 text-xs font-medium rounded-md transition-colors duration-150 ${
                filter === option.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={option.icon} size={12} />
              <span className="hidden sm:inline">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Activity List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-150"
            >
              <div className={`flex items-center justify-center w-8 h-8 rounded-full ${activity.iconBg} flex-shrink-0`}>
                <Icon name={activity.icon} size={16} className={activity.iconColor} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-medium text-foreground truncate">
                    {activity.title}
                  </h4>
                  <span className="text-xs text-muted-foreground ml-2 flex-shrink-0">
                    {formatTimeAgo(activity.timestamp)}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-1">
                  {activity.description}
                </p>
                
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-muted-foreground">
                    by {activity.user}
                  </span>
                  <div className={`w-1 h-1 rounded-full ${
                    activity.type === 'transaction' ? 'bg-success' :
                    activity.type === 'user' ? 'bg-primary' : 'bg-secondary'
                  }`} />
                  <span className="text-xs text-muted-foreground capitalize">
                    {activity.type}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{filteredActivities.length} activities</span>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
            <span>Live updates</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityFeed;